import { increment, onDisconnect, onValue, push, ref, set, update } from 'firebase/database';
import { db, signInStatsViewer } from './firebase';

function getBrowserInfo() {
  const ua = navigator.userAgent || '';
  const platform = navigator.userAgentData?.platform || navigator.platform || 'unknown';

  const browser =
    ua.includes('Edg/') ? 'Edge' :
    ua.includes('Chrome/') ? 'Chrome' :
    ua.includes('Safari/') && !ua.includes('Chrome/') ? 'Safari' :
    ua.includes('Firefox/') ? 'Firefox' :
    'unknown';

  const os =
    /iPhone|iPad|iPod/i.test(ua) ? 'iOS' :
    /Android/i.test(ua) ? 'Android' :
    /Windows/i.test(platform) ? 'Windows' :
    /Mac/i.test(platform) ? 'macOS' :
    /Linux/i.test(platform) ? 'Linux' :
    platform || 'unknown';

  return {
    device: platform || 'web',
    os,
    browser,
    locale: navigator.language || 'unknown',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown'
  };
}

async function fetchJson(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url} failed with ${response.status}`);
  return response.json();
}

async function resolveServerSessionInfo() {
  const session = await fetchJson('/api/track-session');
  return {
    device: session.device || 'web',
    os: session.os || 'unknown',
    browser: session.browser || 'unknown',
    country: session.country || 'unknown',
    countryCode: session.countryCode || 'unknown',
    city: session.city || session.region || session.country || 'unknown',
    ip: session.ip || 'unknown',
    region: session.region || session.city || session.country || 'unknown',
    isp: session.isp || 'unknown',
    geoProvider: session.geoProvider || 'cloudflare',
    ...(session.geoError ? { geoError: session.geoError } : {})
  };
}

async function resolveGeoInfo() {
  const providers = [
    {
      name: 'cloudflare',
      url: '/api/geo',
      normalize: (geo) => ({
        country: geo.country || 'unknown',
        countryCode: geo.countryCode || geo.country || 'unknown',
        city: geo.city || 'unknown',
        ip: geo.ip || 'unknown',
        region: geo.region || 'unknown',
        isp: geo.isp || 'unknown',
        geoProvider: 'cloudflare'
      })
    },
    {
      name: 'ipwho.is',
      url: 'https://ipwho.is/',
      normalize: (geo) => {
        if (geo.success === false) throw new Error(geo.message || 'ipwho.is failed');
        return {
          country: geo.country || 'unknown',
          countryCode: geo.country_code || 'unknown',
          city: geo.city || 'unknown',
          ip: geo.ip || 'unknown',
          region: geo.region || 'unknown',
          isp: geo.connection?.isp || geo.connection?.org || 'unknown',
          geoProvider: 'ipwho.is'
        };
      }
    },
    {
      name: 'ipapi.co',
      url: 'https://ipapi.co/json/',
      normalize: (geo) => ({
        country: geo.country_name || 'unknown',
        countryCode: geo.country_code || 'unknown',
        city: geo.city || 'unknown',
        ip: geo.ip || 'unknown',
        region: geo.region || 'unknown',
        isp: geo.org || 'unknown',
        geoProvider: 'ipapi.co'
      })
    },
    {
      name: 'api.country.is',
      url: 'https://api.country.is/',
      normalize: (geo) => ({
        country: geo.country || 'unknown',
        countryCode: geo.country || 'unknown',
        city: 'unknown',
        ip: geo.ip || 'unknown',
        region: 'unknown',
        isp: 'unknown',
        geoProvider: 'api.country.is'
      })
    }
  ];

  const errors = [];
  for (const provider of providers) {
    try {
      const parsed = provider.normalize(await fetchJson(provider.url));
      if (parsed.country && parsed.country !== 'unknown') return parsed;
      errors.push(`${provider.name}: empty country`);
    } catch (error) {
      errors.push(`${provider.name}: ${error.message}`);
    }
  }

  return {
    country: 'unknown',
    countryCode: 'unknown',
    city: 'unknown',
    ip: 'unknown',
    region: 'unknown',
    isp: 'unknown',
    geoProvider: 'none',
    geoError: errors.join(' | ')
  };
}

let presenceListenerUnsub = null;
let presenceHeartbeatTimer = null;
const PRESENCE_HEARTBEAT_MS = 30000;

function trackPresence(uid, source, info = {}) {
  // Cancel any previous listener before creating a new one (e.g. when geo info arrives).
  if (presenceListenerUnsub) {
    presenceListenerUnsub();
    presenceListenerUnsub = null;
  }
  if (presenceHeartbeatTimer) {
    window.clearInterval(presenceHeartbeatTimer);
    presenceHeartbeatTimer = null;
  }

  const presenceRef = ref(db, `presence/${uid}`);

  // Persistent listener: re-registers onDisconnect and re-sets online:true on every
  // (re)connect. Without this, a brief WebSocket drop fires onDisconnect→online:false
  // and the user stays "offline" for the rest of the session.
  presenceListenerUnsub = onValue(ref(db, '.info/connected'), (snap) => {
    if (!snap.val()) return;

    const now = Date.now();
    const offlineState = { uid, source, online: false, last_seen: now, ...info };

    onDisconnect(presenceRef)
      .set(offlineState)
      .then(() => {
        set(presenceRef, { ...offlineState, online: true, connected_at: now });
        presenceHeartbeatTimer = window.setInterval(() => {
          update(presenceRef, { online: true, last_seen: Date.now() }).catch((error) =>
            console.warn('presence heartbeat failed:', error)
          );
        }, PRESENCE_HEARTBEAT_MS);
      })
      .catch((error) => console.warn('presence tracking failed:', error));
  });
}

async function updateStatsSafely(payload) {
  try {
    await update(ref(db, 'stats'), payload);
  } catch (error) {
    console.warn('web stats update failed:', error);
  }
}

export async function startWebPlayingPresence(info = {}) {
  const uid = await signInStatsViewer();
  if (!db || !uid) return () => {};

  const now = Date.now();
  const browserInfo = getBrowserInfo();
  const { device, os, browser } = browserInfo;
  const playingRef = ref(db, `playing_presence/${uid}`);
  const baseState = {
    uid,
    source: 'web',
    online: false,
    last_seen: now,
    device,
    os,
    browser,
    ...info
  };

  await onDisconnect(playingRef).set(baseState);
  await set(playingRef, {
    ...baseState,
    online: true,
    connected_at: now
  });

  const heartbeatTimer = window.setInterval(() => {
    update(playingRef, { online: true, last_seen: Date.now() }).catch((error) =>
      console.warn('web playing presence heartbeat failed:', error)
    );
  }, PRESENCE_HEARTBEAT_MS);

  Promise.resolve()
    .then(async () => {
      let serverInfo = null;
      try {
        serverInfo = await resolveServerSessionInfo();
      } catch (error) {
        console.warn('web playing presence geo fallback:', error);
      }
      const geo = serverInfo || await resolveGeoInfo();
      await set(playingRef, {
        ...baseState,
        online: true,
        connected_at: now,
        country: geo.country || 'unknown',
        countryCode: geo.countryCode || 'unknown',
        city: geo.city || 'unknown',
        region: geo.region || 'unknown',
        isp: geo.isp || 'unknown',
        ip: geo.ip || 'unknown'
      });
    })
    .catch((error) => console.warn('web playing presence geo update failed:', error));

  return () => {
    const stoppedAt = Date.now();
    window.clearInterval(heartbeatTimer);
    onDisconnect(playingRef).cancel().catch((error) => console.warn('web playing presence cancel failed:', error));
    set(playingRef, {
      ...baseState,
      online: false,
      last_seen: stoppedAt,
      disconnected_at: stoppedAt
    }).catch((error) => console.warn('web playing presence stop failed:', error));
  };
}

export async function trackWebSession() {
  try {
    const uid = await signInStatsViewer();
    if (!db || !uid) return;

    const browserInfo = getBrowserInfo();
    trackPresence(uid, 'web', browserInfo);

    let serverInfo = null;

    try {
      serverInfo = await resolveServerSessionInfo();
    } catch (error) {
      console.warn('server session tracking fallback:', error);
    }

    const geo = serverInfo || await resolveGeoInfo();
    const now = Date.now();
    trackPresence(uid, 'web', {
      ...browserInfo,
      country: geo.country || 'unknown',
      countryCode: geo.countryCode || 'unknown',
      city: geo.city || 'unknown',
      region: geo.region || 'unknown',
      isp: geo.isp || 'unknown',
      ip: geo.ip || 'unknown'
    });

    await Promise.all([
      push(ref(db, 'sessions'), {
        uid,
        source: 'web',
        created_at: now,
        ...browserInfo,
        ...geo
      }),
      updateStatsSafely({
        total_opens: increment(1),
        opens_web: increment(1),
        last_open: now
      })
    ]);
  } catch (error) {
    console.warn('web session tracking failed:', error);
  }
}

export async function trackWebEvent(type, payload = {}) {
  try {
    const uid = await signInStatsViewer();
    if (!db || !uid) return;

    await push(ref(db, 'events'), {
      uid,
      type,
      source: 'web',
      created_at: Date.now(),
      ...payload
    });
  } catch (error) {
    console.warn('web event tracking failed:', error);
  }
}
